<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateInvoice',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    invoiceKind: {
      type: String,
      required: false,
      default: 'outgoing',
    },
    invoiceType: {
      type: String,
      required: false,
      default: 'normal',
    },
    registerEntry: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelAlign = ref('right');
  const titles = ref({
    incoming: $fn.value.pgettext('Invoice', 'Create Incoming Invoice'),
    outgoing: $fn.value.pgettext('Invoice', 'Create Outgoing Invoice'),
  });
  const formId = ref(null);
  const item = ref(null);
  const formContext = ref({});
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    titles: titles.value,
    formId: formId.value,
    item: item.value,
    formContext: formContext.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = () => {
    return $fn.value
      .fetch('get_invoice_template', {
        parameters: {
          invoice_kind: props.invoiceKind,
          invoice_type: props.invoiceType,
          register_entry: props.registerEntry,
        },
      })
      .then(
        (data) =>
          new $fn.value.Resource(data, {
            billing_period: data.billing_period || { start: null, end: null },
          })
      );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'titles' },
      { name: 'formId' },
      { name: 'item' },
      { name: 'formContext' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId, item: $$load_item },
    ux: {},
    dataRefs: {
      labelAlign,
      titles,
      formId,
      item,
      formContext,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const incomingInvoice = computed(() => {
    return props.invoiceKind === 'incoming';
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return { title: titles.value[props.invoiceKind] };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.invoiceKind,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoiceKind');
    }
  );
  watch(
    () => props.invoiceType,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoiceType');
    }
  );
  watch(
    () => props.registerEntry,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('registerEntry');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_4tb5n68jt_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_8564662037462133_reloadGrid_i8xpd6ae1_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'invoices',
    };
  };
  const act_1040167445267876_closeModal_q513glmas_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };
  const act_1040167445267876_navigate_a473agt1g_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.invoice',
        params: {
          id: $event.data.id,
        },
      },
    };
  };
  const act_1040167445267876_crud_4t7gf0msk_getActionArgs = async ($event) => {
    return {
      objectType: 'invoice',
      op: 'upsert',
      data: item.value,
    };
  };
  const act_2434514588044777_closeModal_dqw6frtt0_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_4tb5n68jt =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_4tb5n68jt_getActionArgs,
    });
  const inst_evh_7315092382398562_close_zsu2xhcab =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_4tb5n68jt,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_zsu2xhcab],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_segb2cbrk =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_hulatkd5d =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_segb2cbrk,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_hulatkd5d],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_dzal7n15u =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_02tev0206 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_dzal7n15u,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_02tev0206],
    event: 'close',
  });
  const act_8564662037462133_closeModal_frl9izvlr =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_deleted_965at9des =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_frl9izvlr,
      event: 'deleted',
      displayName: 'closeModal',
    });
  const act_8564662037462133_reloadGrid_i8xpd6ae1 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_8564662037462133_reloadGrid_i8xpd6ae1_getActionArgs,
    });
  const inst_evh_8564662037462133_deleted_ghlax8pjl =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_reloadGrid_i8xpd6ae1,
      event: 'deleted',
      displayName: 'reloadGrid',
    });
  const inst_evh_8564662037462133_deleted = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_8564662037462133_deleted_965at9des,
      inst_evh_8564662037462133_deleted_ghlax8pjl,
    ],
    event: 'deleted',
  });
  const act_1040167445267876_closeModal_q513glmas =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_q513glmas_getActionArgs,
    });
  const inst_evh_1040167445267876_success_8n1odnyw6 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_q513glmas,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_navigate_a473agt1g =
    new actionTypes.NavigateAction({
      actionArgs: act_1040167445267876_navigate_a473agt1g_getActionArgs,
    });
  const inst_evh_1040167445267876_success_byj4vtl0q =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_navigate_a473agt1g,
      event: 'success',
      displayName: 'navigate',
    });
  const act_1040167445267876_crud_4t7gf0msk = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_4t7gf0msk_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_1040167445267876_success_8n1odnyw6,
      inst_evh_1040167445267876_success_byj4vtl0q,
    ],
  });
  const inst_evh_1040167445267876_submit_k2509lkri =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_4t7gf0msk,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_k2509lkri],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_dqw6frtt0 =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_dqw6frtt0_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_35u1zyz54 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_dqw6frtt0,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_35u1zyz54],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_zsu2xhcab,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_hulatkd5d,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_02tev0206,
    inst_evh_8564662037462133_close,
    inst_evh_8564662037462133_deleted_965at9des,
    inst_evh_8564662037462133_deleted_ghlax8pjl,
    inst_evh_8564662037462133_deleted,
    inst_evh_1040167445267876_success_8n1odnyw6,
    inst_evh_1040167445267876_success_byj4vtl0q,
    inst_evh_1040167445267876_submit_k2509lkri,
    submit: inst_evh_1040167445267876_submit_k2509lkri,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_35u1zyz54,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_zsu2xhcab = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_zsu2xhcab.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_hulatkd5d = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_hulatkd5d.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_02tev0206 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_02tev0206.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_deleted_965at9des = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_deleted_965at9des.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_deleted_ghlax8pjl = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_deleted_ghlax8pjl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_deleted = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_deleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_8n1odnyw6 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_8n1odnyw6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_byj4vtl0q = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_byj4vtl0q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_k2509lkri = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_k2509lkri.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_35u1zyz54 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_35u1zyz54.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateInvoice"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :color="$config.invoice.color"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
        @deleted="evh_8564662037462133_deleted($event, {})"
      >
        <UcInvoiceHeader
          :invoice="_.clone(item)"
          :formId="formId"
          :formContext="formContext"
          :invoiceNumber="registerEntry ? item.reference_number : null"
        >
        </UcInvoiceHeader>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="invoice"
        autofocus="first"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetInvoiceHeader
          :formId="formId"
          :invoice="item"
          :labelAlign="labelAlign"
        >
        </FieldsetInvoiceHeader>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter
        :card="!modal"
        :modal="modal"
        :color="$config.invoice.color"
        v-if="loaded"
      >
        <ItpButton
          :form="forms.invoice && forms.invoice.id"
          icon="fa-save"
          :text="pgettext('CreateInvoice|Submit', 'Create invoice')"
          type="submit"
          variant="primary"
          :spinning="actions.submit.isRunning"
          :disabled="!forms.invoice || !forms.invoice.submittable"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
